import { useEffect, useRef, useState } from 'react';
import { Icon } from '../ui/Icon';
import RichTextEditor from '../ui/RichTextEditor';
import { useToast } from '../ui/Toast';

interface Props {
  value: string;
  onSave: (next: string) => Promise<void>;
  placeholder?: string;
  label?: string;
}

export const NotesCard = ({
  value,
  onSave,
  placeholder = 'Add an internal note — what we did, what we learned, what to repeat next year…',
  label = 'Internal note',
}: Props) => {
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const lastSaved = useRef(value);
  const draftRef = useRef(value);
  const toast = useToast();

  useEffect(() => {
    setDraft(value);
    lastSaved.current = value;
    draftRef.current = value;
  }, [value]);

  const commit = async () => {
    const next = draftRef.current;
    if (next === lastSaved.current) return;
    setSaving(true);
    try {
      await onSave(next);
      lastSaved.current = next;
      setSavedAt(Date.now());
    } catch {
      toast.push('Could not save the note');
      setDraft(lastSaved.current);
      draftRef.current = lastSaved.current;
    } finally {
      setSaving(false);
    }
  };

  const justSaved = savedAt && Date.now() - savedAt < 3000;

  let status: React.ReactNode = null;
  if (saving)
    status = <span style={{ fontSize: 10.5, color: 'var(--ink-4)' }}>Saving…</span>;
  else if (justSaved)
    status = (
      <span
        style={{
          fontSize: 10.5,
          color: 'var(--seasonal)',
          display: 'inline-flex',
          gap: 3,
          alignItems: 'center',
        }}
      >
        <Icon name="check" size={10} /> Saved
      </span>
    );

  return (
    <div className="notes-aside">
      <div className="notes-aside-head">
        <span className="notes-aside-label">{label}</span>
        {status}
      </div>
      <RichTextEditor
        value={draft}
        onChange={(html) => {
          setDraft(html);
          draftRef.current = html;
        }}
        onBlur={commit}
        placeholder={placeholder}
      />
    </div>
  );
};

export default NotesCard;
